// src/components/ExpenseReportScreen.jsx
// Relatório de despesas premium.
// Rota: /reports/expense
// Endpoint: GET /psy/finance/expense?id=ID_USUARIO
// UI isolada em .exprep-* para não colidir com telas que já usam .exp-*.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/expense-report.scss";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const EXPENSE_FILTERS = [
  { value: "todas", label: "Todas" },
  { value: "aluguel", label: "Aluguel" },
  { value: "outros", label: "Outros" },
];

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function saveAuthToken(token) {
  if (!token) return;

  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function brl(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(safeNumber(value));
}

function percentBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(safeNumber(value));
}

function parseDate(value) {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateBR(value) {
  const date = parseDate(value);

  if (!date) return "Não informado";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function getTimeBR(value) {
  const date = parseDate(value);

  if (!date) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();
  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

function getMonthLabel(value, index = 0) {
  const month = Number(value);

  if (Number.isFinite(month)) {
    if (month >= 1 && month <= 12) return MONTHS[month - 1];
    if (month >= 0 && month <= 11) return MONTHS[month];
  }

  return MONTHS[index % 12];
}

function getExpenseCategory(item) {
  return String(item?.categoria || item?.servicoId || item?.category || "Despesa").trim() || "Despesa";
}

function getExpenseValue(item) {
  return safeNumber(item?.valor ?? item?.valorTotal ?? item?.total ?? item?.value ?? 0);
}

function getExpenseDate(item) {
  return item?.data || item?.dataCadastro || item?.createdAt || item?.vencimento || null;
}

function getExpenseDescription(item) {
  return String(item?.descricao || item?.description || getExpenseCategory(item) || "Despesa").trim();
}

function isAluguelCategory(category = "") {
  return normalizeText(category).includes("aluguel");
}

function normalizeEvolution(data = []) {
  const rows = Array.isArray(data) ? data : [];

  return rows
    .map((item, index) => {
      const valor = safeNumber(item?.valor ?? item?.total ?? item?.despesa ?? 0);
      const mes = item?.mes ?? item?.month ?? index;

      return {
        label: item?.label || item?.nome || getMonthLabel(mes, index),
        valor,
      };
    })
    .filter((item) => Number.isFinite(item.valor));
}

function normalizeLastExpenses(data = []) {
  const rows = Array.isArray(data) ? data : [];

  return rows.map((item, index) => ({
    id: item?._id || item?.id || `${index}`,
    categoria: getExpenseCategory(item),
    descricao: getExpenseDescription(item),
    valor: getExpenseValue(item),
    data: getExpenseDate(item),
  }));
}

function buildExpenseSearchText(item) {
  return normalizeText(
    [
      item.categoria,
      item.descricao,
      brl(item.valor),
      formatDateBR(item.data),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return payload?.message || payload?.error || JSON.stringify(payload);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function getExpenseHealth(total) {
  const value = safeNumber(total);

  if (value <= 0) {
    return {
      label: "Sem despesas",
      tone: "good",
      icon: "✓",
      description: "Nenhuma saída registrada no período.",
    };
  }

  if (value <= 1000) {
    return {
      label: "Controlado",
      tone: "good",
      icon: "✓",
      description: "Os gastos estão em uma faixa saudável.",
    };
  }

  if (value <= 3000) {
    return {
      label: "Atenção",
      tone: "warning",
      icon: "!",
      description: "Acompanhe custos recorrentes e parcelas.",
    };
  }

  return {
    label: "Alto impacto",
    tone: "bad",
    icon: "!",
    description: "As despesas estão pressionando o resultado.",
  };
}

function DonutChart({ aluguel, outros }) {
  const total = safeNumber(aluguel) + safeNumber(outros);
  const aluguelPercent = total > 0 ? (safeNumber(aluguel) / total) * 100 : 0;
  const outrosPercent = total > 0 ? (safeNumber(outros) / total) * 100 : 0;

  return (
    <div className="exprep-pie">
      <div className="exprep-donut" role="img" aria-label="Distribuição de gastos">
        <div
          className="exprep-donut__ring"
          style={{
            background: total
              ? `conic-gradient(var(--exprep-blue-500) 0 ${aluguelPercent}%, var(--exprep-amber-500) ${aluguelPercent}% 100%)`
              : "conic-gradient(var(--exprep-slate-200) 0 100%)",
          }}
        />

        <div className="exprep-donut__hole">
          <strong>{brl(total)}</strong>
          <span>Total distribuído</span>
        </div>
      </div>

      <div className="exprep-legend">
        <LegendRow
          color="blue"
          label="Aluguel"
          value={brl(aluguel)}
          percent={`${percentBR(aluguelPercent)}%`}
        />

        <LegendRow
          color="amber"
          label="Outros"
          value={brl(outros)}
          percent={`${percentBR(outrosPercent)}%`}
        />
      </div>
    </div>
  );
}

function LegendRow({ color, label, value, percent }) {
  return (
    <div className="exprep-legend__row">
      <span className={`exprep-dot exprep-dot--${color}`} aria-hidden="true" />
      <div>
        <strong>{label}</strong>
        <small>{percent}</small>
      </div>
      <span>{value}</span>
    </div>
  );
}

function EvolutionChart({ data = [] }) {
  const rows = normalizeEvolution(data);

  if (!rows.length) {
    return (
      <div className="exprep-chart-empty">
        <span>📉</span>
        <strong>Sem evolução de gastos.</strong>
        <small>Quando houver dados mensais, o gráfico aparecerá aqui.</small>
      </div>
    );
  }

  const visibleRows = rows.slice(0, 12);
  const maxValue = Math.max(...visibleRows.map((item) => item.valor), 1);
  const maxY = maxValue * 1.18;

  const width = 100;
  const height = 46;
  const left = 5;
  const right = 96;
  const top = 7;
  const base = 39;
  const chartWidth = right - left;
  const gap = 1.35;
  const barWidth = Math.max(2, (chartWidth - gap * (visibleRows.length - 1)) / visibleRows.length);

  const points = visibleRows.map((item, index) => {
    const x = left + index * (barWidth + gap) + barWidth / 2;
    const y = base - (Math.max(0, item.valor) / maxY) * (base - top);
    return `${x},${y}`;
  });

  return (
    <div className="exprep-chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="exprep-svg"
        role="img"
        aria-label="Evolução dos gastos"
        preserveAspectRatio="none"
      >
        {[12, 20, 28, 36].map((y) => (
          <line key={y} x1="0" y1={y} x2="100" y2={y} className="exprep-grid-line" />
        ))}

        <path d={`M ${left} ${base} L ${right} ${base}`} className="exprep-axis" />

        {visibleRows.map((item, index) => {
          const value = Math.max(0, item.valor);
          const barHeight = (value / maxY) * (base - top);
          const x = left + index * (barWidth + gap);
          const y = base - barHeight;

          return (
            <rect
              key={`${item.label}-${index}`}
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              rx="1.2"
              className="exprep-bar"
            >
              <title>
                {item.label}: {brl(item.valor)}
              </title>
            </rect>
          );
        })}

        {points.length > 1 ? (
          <polyline points={points.join(" ")} className="exprep-line-path" />
        ) : null}

        {points.map((point, index) => {
          const [x, y] = point.split(",").map(Number);

          return (
            <circle
              key={`${point}-${index}`}
              cx={x}
              cy={y}
              r="1.35"
              className="exprep-line-dot"
            />
          );
        })}
      </svg>

      <div className="exprep-xlabels">
        {visibleRows.map((item, index) => (
          <span key={`${item.label}-${index}`}>{item.label}</span>
        ))}
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, description, tone = "blue" }) {
  return (
    <article className={`exprep-kpi exprep-kpi--${tone}`}>
      <div className="exprep-kpi__icon" aria-hidden="true">
        {icon}
      </div>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function ExpenseReportSkeleton() {
  return (
    <div className="exprep-skeleton" aria-hidden="true">
      <div className="exprep-skeleton__hero" />
      <div className="exprep-skeleton__kpis">
        <span />
        <span />
        <span />
      </div>
      <div className="exprep-skeleton__panel" />
      <div className="exprep-skeleton__list">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function EmptyState({ title, message, onRetry, onBack }) {
  return (
    <section className="exprep-state">
      <div className="exprep-state__icon">💸</div>
      <strong>{title}</strong>
      <span>{message}</span>

      <div className="exprep-state__actions">
        <button className="exprep-btn exprep-btn--ghost-dark" type="button" onClick={onBack}>
          Voltar
        </button>

        <button className="exprep-btn exprep-btn--primary" type="button" onClick={onRetry}>
          Tentar novamente
        </button>
      </div>
    </section>
  );
}

export default function ExpenseReportScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [total, setTotal] = useState(0);
  const [aluguel, setAluguel] = useState(0);
  const [outros, setOutros] = useState(0);
  const [evolucao, setEvolucao] = useState([]);
  const [ultimas, setUltimas] = useState([]);
  const [error, setError] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [expenseFilter, setExpenseFilter] = useState("todas");
  const [search, setSearch] = useState("");

  const nomeUsuario = useMemo(() => readStorage([AUTH_KEYS.USER_NAME]) || "", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const authOrRedirect = useCallback(() => {
    const token = readStorage([AUTH_KEYS.TOKEN, "token"]);
    const userId = readStorage([AUTH_KEYS.USER_ID, "id"]);

    if (!userId) {
      toast.error("ID do usuário não encontrado. Faça login novamente.");
      navigate("/login", { replace: true });
      return null;
    }

    if (!token) {
      toast.error("Token não encontrado. Faça login novamente.");
      navigate("/login", { replace: true });
      return null;
    }

    return { token, userId };
  }, [navigate]);

  const fetchData = useCallback(
    async ({ signal, silent = false } = {}) => {
      const auth = authOrRedirect();

      if (!auth) {
        setLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const response = await fetch(
          `${API_BASE}/psy/finance/expense?id=${encodeURIComponent(auth.userId)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
            signal,
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          toast.error("Sessão expirada. Faça login novamente.");
          navigate("/login", { replace: true });
          return;
        }

        if (!response.ok) {
          const message = await readApiError(response);
          setError(message || "Erro ao carregar dados.");
          return;
        }

        const payload = await response.json();

        setTotal(safeNumber(payload?.total ?? 0));
        setAluguel(safeNumber(payload?.aluguel ?? 0));
        setOutros(safeNumber(payload?.outros ?? 0));
        setEvolucao(Array.isArray(payload?.evolucaoMensal) ? payload.evolucaoMensal : []);
        setUltimas(Array.isArray(payload?.ultimasDespesas) ? payload.ultimasDespesas : []);
        setLastUpdatedAt(new Date());
      } catch (err) {
        if (err?.name === "AbortError") return;

        console.error("Erro ao carregar relatório de despesas:", err);
        setError("Falha de comunicação com a API.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authOrRedirect, navigate]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchData({ signal: controller.signal });

    return () => controller.abort();
  }, [fetchData]);

  const normalizedExpenses = useMemo(() => normalizeLastExpenses(ultimas), [ultimas]);

  const filteredExpenses = useMemo(() => {
    const query = normalizeText(search);

    return normalizedExpenses.filter((expense) => {
      const categoryIsAluguel = isAluguelCategory(expense.categoria);
      const matchesFilter =
        expenseFilter === "todas" ||
        (expenseFilter === "aluguel" && categoryIsAluguel) ||
        (expenseFilter === "outros" && !categoryIsAluguel);

      const matchesSearch = !query || buildExpenseSearchText(expense).includes(query);

      return matchesFilter && matchesSearch;
    });
  }, [normalizedExpenses, expenseFilter, search]);

  const pie = useMemo(() => {
    const aluguelValue = safeNumber(aluguel);
    const outrosValue = safeNumber(outros);
    const sum = aluguelValue + outrosValue;

    return {
      aluguel: aluguelValue,
      outros: outrosValue,
      sum,
      aluguelPercent: sum > 0 ? (aluguelValue / sum) * 100 : 0,
      outrosPercent: sum > 0 ? (outrosValue / sum) * 100 : 0,
    };
  }, [aluguel, outros]);

  const biggerCategory = useMemo(() => {
    if (pie.sum <= 0) return "Sem dados";
    return pie.aluguel >= pie.outros ? "Aluguel" : "Outros";
  }, [pie]);

  const averageExpense = useMemo(() => {
    if (!normalizedExpenses.length) return 0;

    const sum = normalizedExpenses.reduce((acc, item) => acc + safeNumber(item.valor), 0);

    return sum / normalizedExpenses.length;
  }, [normalizedExpenses]);

  const health = useMemo(() => getExpenseHealth(total), [total]);

  const reload = () => fetchData({ silent: true });

  const goBack = () => navigate("/reports");

  const goFinance = () => navigate("/finance");

  const printReport = () => window.print();

  return (
    <div className="exprep-page">
      <div className="exprep-bg" aria-hidden="true">
        <span className="exprep-bg__orb exprep-bg__orb--one" />
        <span className="exprep-bg__orb exprep-bg__orb--two" />
        <span className="exprep-bg__orb exprep-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="exprep-topbar">
        <button
          className="exprep-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          ☰
        </button>

        <div className="exprep-brand">
          <img src={Logo} alt="PsyAssist" className="exprep-brand__logo" />
          <div className="exprep-brand__text">
            <strong>Relatório de despesas</strong>
            <span>Resumo, distribuição e evolução • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="exprep-top-actions">
          <button
            className="exprep-btn exprep-btn--ghost exprep-btn--desktop"
            type="button"
            onClick={goBack}
          >
            Voltar
          </button>

          <button
            className="exprep-btn exprep-btn--ghost"
            type="button"
            onClick={printReport}
            disabled={loading || Boolean(error)}
          >
            Imprimir
          </button>

          <button
            className="exprep-btn exprep-btn--primary"
            type="button"
            onClick={reload}
            disabled={loading || refreshing}
          >
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="exprep-main">
        {loading ? (
          <ExpenseReportSkeleton />
        ) : error ? (
          <EmptyState
            title="Não foi possível carregar o relatório."
            message={error}
            onRetry={reload}
            onBack={goBack}
          />
        ) : (
          <>
            <section className="exprep-hero">
              <div>
                <span className="exprep-kicker">Controle de saídas</span>
                <h1>Despesas</h1>
                <p>
                  {firstName ? `${firstName}, ` : ""}
                  acompanhe o total de gastos, a distribuição por categoria e a
                  evolução das despesas do consultório.
                </p>
              </div>

              <aside className={`exprep-hero-card exprep-hero-card--${health.tone}`}>
                <div className="exprep-hero-card__icon" aria-hidden="true">
                  {health.icon}
                </div>

                <div>
                  <small>Status do período</small>
                  <strong>{health.label}</strong>
                  <span>{health.description}</span>
                </div>
              </aside>
            </section>

            <section className="exprep-kpis">
              <KpiCard
                icon="⛔"
                label="Total"
                value={brl(total)}
                description="Despesa total do período"
                tone="red"
              />

              <KpiCard
                icon="🏠"
                label="Aluguel"
                value={brl(aluguel)}
                description={`${percentBR(pie.aluguelPercent)}% da distribuição`}
                tone="blue"
              />

              <KpiCard
                icon="📦"
                label="Outros"
                value={brl(outros)}
                description={`${percentBR(pie.outrosPercent)}% da distribuição`}
                tone="amber"
              />
            </section>

            <section className="exprep-panel">
              <div className="exprep-panel__head">
                <div>
                  <span>Distribuição</span>
                  <h2>Distribuição de gastos</h2>
                  <p>
                    Compare o peso de aluguel e demais despesas no total do
                    período.
                  </p>
                </div>

                <div className="exprep-updated">
                  {lastUpdatedAt ? `Atualizado às ${getTimeBR(lastUpdatedAt)}` : "Atualizado agora"}
                </div>
              </div>

              <div className="exprep-distribution">
                <DonutChart aluguel={aluguel} outros={outros} />

                <aside className="exprep-insight">
                  <span>Maior categoria</span>
                  <strong>{biggerCategory}</strong>
                  <p>
                    Média das últimas despesas: <b>{brl(averageExpense)}</b>.
                    Use essa leitura para identificar custos recorrentes e pontos
                    de economia.
                  </p>

                  <button className="exprep-btn exprep-btn--ghost-dark" type="button" onClick={goFinance}>
                    Abrir financeiro
                  </button>
                </aside>
              </div>
            </section>

            <section className="exprep-panel">
              <div className="exprep-panel__head">
                <div>
                  <span>Evolução</span>
                  <h2>Evolução dos gastos</h2>
                  <p>
                    Visualize como as despesas se comportaram nos últimos períodos.
                  </p>
                </div>
              </div>

              <div className="exprep-chart-card">
                <EvolutionChart data={evolucao} />
              </div>
            </section>

            <section className="exprep-panel">
              <div className="exprep-panel__head exprep-panel__head--with-tools">
                <div>
                  <span>Últimos lançamentos</span>
                  <h2>Últimas despesas</h2>
                  <p>
                    Consulte os lançamentos mais recentes e filtre por categoria.
                  </p>
                </div>

                <div className="exprep-counter">
                  {filteredExpenses.length} de {normalizedExpenses.length}
                </div>
              </div>

              <div className="exprep-toolbar">
                <label className="exprep-search">
                  <span aria-hidden="true">⌕</span>
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar despesa..."
                  />

                  {search ? (
                    <button type="button" onClick={() => setSearch("")} aria-label="Limpar busca">
                      ×
                    </button>
                  ) : null}
                </label>

                <div className="exprep-filter-group" aria-label="Filtro de despesas">
                  {EXPENSE_FILTERS.map((filter) => (
                    <button
                      key={filter.value}
                      type="button"
                      className={expenseFilter === filter.value ? "is-active" : ""}
                      onClick={() => setExpenseFilter(filter.value)}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>

              {filteredExpenses.length === 0 ? (
                <div className="exprep-empty-mini">
                  Nenhuma despesa encontrada com os filtros atuais.
                </div>
              ) : (
                <div className="exprep-list">
                  {filteredExpenses.map((expense) => {
                    const categoryIsAluguel = isAluguelCategory(expense.categoria);

                    return (
                      <article className="exprep-row" key={expense.id}>
                        <div
                          className={`exprep-row__icon ${
                            categoryIsAluguel ? "is-rent" : "is-other"
                          }`}
                          aria-hidden="true"
                        >
                          {categoryIsAluguel ? "🏠" : "💸"}
                        </div>

                        <div className="exprep-row__content">
                          <strong>{expense.descricao || expense.categoria || "Despesa"}</strong>
                          <span>
                            {formatDateBR(expense.data)} • {expense.categoria || "Sem categoria"}
                          </span>
                        </div>

                        <div className="exprep-row__value">{brl(expense.valor)}</div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="exprep-bottom-actions">
              <button className="exprep-btn exprep-btn--ghost-dark" type="button" onClick={goBack}>
                Voltar aos relatórios
              </button>

              <button className="exprep-btn exprep-btn--primary" type="button" onClick={goFinance}>
                Abrir financeiro
              </button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
